// /app/frontend/src/pages/TrackSamplesPage.jsx
// Customer page to track fabric sample request status

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Truck, CheckCircle, Clock, Search,
  Loader2, ChevronRight, ExternalLink, Mail, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Badge } from '../components/ui/badge';
import { brandLabel } from '../lib/brandNames';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-800', step: 1 },
  processing: { label: 'Processing', icon: Package, color: 'bg-blue-100 text-blue-800', step: 2 },
  shipped: { label: 'Shipped', icon: Truck, color: 'bg-purple-100 text-purple-800', step: 3 },
  delivered: { label: 'Delivered', icon: CheckCircle, color: 'bg-green-100 text-green-800', step: 4 },
  cancelled: { label: 'Cancelled', icon: Package, color: 'bg-red-100 text-red-800', step: 0 }
};

const CARRIER_TRACKING_URLS = {
  'USPS': 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
  'UPS': 'https://www.ups.com/track?tracknum=',
  'FedEx': 'https://www.fedex.com/fedextrack/?trknbr=',
  'DHL': 'https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id='
};

const TrackSamplesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/fabric-collections/my-sample-requests`, {
        params: { email: email.trim() }
      });
      setRequests(response.data.requests || []);
      setSearched(true);
      
      if (response.data.total === 0) {
        toast.info('No sample requests found for this email');
      }
    } catch (error) {
      console.error('Failed to fetch sample requests:', error);
      toast.error('Failed to load sample requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTrackingUrl = (carrier, trackingNumber) => {
    const baseUrl = CARRIER_TRACKING_URLS[carrier] || CARRIER_TRACKING_URLS['USPS'];
    return `${baseUrl}${trackingNumber}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="py-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center gap-3 mb-2">
              <Truck className="w-8 h-8 text-amber-600" />
              <h1 className="text-3xl font-bold text-neutral-900">Track Your Samples</h1>
            </div>
            <p className="text-neutral-500">
              Enter your email to view the status of your fabric sample requests.
            </p>
          </div>

          {/* Search Form */}
          <div className="bg-white rounded-xl border border-neutral-200 p-6 mb-8">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="email" className="sr-only">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter the email you used for your sample request"
                    className="pl-11 h-12"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="h-12 px-8 bg-amber-600 hover:bg-amber-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Track Samples
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Results */}
          {searched && (
            <div className="space-y-6">
              {requests && requests.length === 0 ? (
                <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center">
                  <Package className="w-16 h-16 mx-auto text-neutral-200 mb-4" />
                  <h2 className="text-xl font-semibold text-neutral-700 mb-2">
                    No Sample Requests Found
                  </h2>
                  <p className="text-neutral-500 mb-6">
                    We couldn't find any sample requests associated with this email.
                  </p>
                  <Button
                    onClick={() => navigate('/request-samples')}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Request Samples
                  </Button>
                </div>
              ) : (
                <AnimatePresence>
                  {requests?.map((request, index) => {
                    const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <motion.div
                        key={request.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white rounded-xl border border-neutral-200 overflow-hidden"
                      >
                        {/* Request Header */}
                        <div className="p-4 border-b border-neutral-100 bg-neutral-50 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                            <span className="text-sm text-neutral-500">
                              Requested {formatDate(request.created_at)}
                            </span>
                          </div>
                          <span className="text-xs text-neutral-400 font-mono">
                            ID: {request.id?.slice(0, 8)}...
                          </span>
                        </div>

                        {/* Progress Bar */}
                        {request.status !== 'cancelled' && (
                          <div className="px-4 py-6 border-b border-neutral-100">
                            <div className="flex items-center justify-between max-w-md mx-auto">
                              {['Pending', 'Processing', 'Shipped', 'Delivered'].map((step, i) => {
                                const stepNum = i + 1;
                                const isActive = statusConfig.step >= stepNum;
                                const isCurrent = statusConfig.step === stepNum;
                                
                                return (
                                  <React.Fragment key={step}>
                                    <div className="flex flex-col items-center">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                                        isActive 
                                          ? 'bg-amber-600 text-white' 
                                          : 'bg-neutral-200 text-neutral-400'
                                      } ${isCurrent ? 'ring-4 ring-amber-100' : ''}`}>
                                        {isActive ? '✓' : stepNum}
                                      </div>
                                      <span className={`text-xs mt-1 ${isActive ? 'text-amber-600 font-medium' : 'text-neutral-400'}`}>
                                        {step}
                                      </span>
                                    </div>
                                    {i < 3 && (
                                      <div className={`flex-1 h-1 mx-2 rounded ${
                                        statusConfig.step > stepNum ? 'bg-amber-600' : 'bg-neutral-200'
                                      }`} />
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Tracking Info */}
                        {request.tracking_number && (
                          <div className="px-4 py-4 border-b border-neutral-100 bg-purple-50">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-purple-700 font-medium">Tracking Number</p>
                                <p className="text-lg font-mono font-bold text-purple-900">
                                  {request.tracking_number}
                                </p>
                                <p className="text-xs text-purple-600 mt-1">
                                  Carrier: {request.carrier || 'USPS'}
                                  {request.shipped_at && ` • Shipped ${formatDate(request.shipped_at)}`}
                                </p>
                              </div>
                              <a
                                href={getTrackingUrl(request.carrier || 'USPS', request.tracking_number)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                              >
                                <ExternalLink className="w-4 h-4" />
                                Track Package
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Colors */}
                        <div className="p-4">
                          <p className="text-sm font-medium text-neutral-700 mb-3">
                            Samples Requested ({request.colors?.length || 0})
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {request.colors?.map((color, colorIndex) => (
                              <div
                                key={colorIndex}
                                className="flex items-center gap-2 bg-neutral-50 rounded-lg p-2 pr-3"
                              >
                                <div
                                  className="w-10 h-10 rounded-lg border border-neutral-200 overflow-hidden"
                                  style={{ backgroundColor: color.hex_color || '#E5E5E5' }}
                                >
                                  {color.image_url && (
                                    <img
                                      src={color.image_url}
                                      alt={color.name}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-neutral-900">{color.name}</p>
                                  <p className="text-xs text-neutral-500">
                                    {brandLabel(color.brand_name)}
                                    {color.material_name && ` • ${color.material_name}`}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>
          )}

          {/* Help Text */}
          {!searched && (
            <div className="text-center text-neutral-500 text-sm">
              <p>
                Don't have a sample request yet?{' '}
                <button
                  onClick={() => navigate('/products/roller-shades')}
                  className="text-amber-600 hover:underline"
                >
                  Browse our fabrics
                </button>
                {' '}and request free samples!
              </p>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TrackSamplesPage;
